/* ==========================================================================
   Luméra · Shared client-side behaviour
   --------------------------------------------------------------------------
   Plain-JS web components — they enhance Liquid-rendered markup and degrade
   gracefully if JS is disabled. Loaded with `defer` from theme.liquid.
   ========================================================================== */
(function () {
  'use strict';

  /* ---------- Reveal on scroll ----------------------------------------- */
  if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('is-in');
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );
    const watch = () => document.querySelectorAll('.lum-reveal:not(.is-in)').forEach((el) => io.observe(el));
    watch();
    document.addEventListener('shopify:section:load', watch);
  } else {
    document.querySelectorAll('.lum-reveal').forEach((el) => el.classList.add('is-in'));
  }

  /* ---------- <lum-accordion> ----------------------------------------- */
  class LumAccordion extends HTMLElement {
    connectedCallback() {
      this.allowMultiple = this.hasAttribute('multiple');
      this.addEventListener('click', this.onClick.bind(this));
    }
    onClick(e) {
      const head = e.target.closest('.lum-acc__head');
      if (!head) return;
      const item = head.closest('.lum-acc__item');
      const isOpen = item.dataset.open === 'true';
      if (!this.allowMultiple) {
        this.querySelectorAll('.lum-acc__item').forEach((i) => {
          i.dataset.open = 'false';
          i.querySelector('.lum-acc__head').setAttribute('aria-expanded', 'false');
        });
      }
      item.dataset.open = String(!isOpen);
      head.setAttribute('aria-expanded', String(!isOpen));
    }
  }
  customElements.define('lum-accordion', LumAccordion);

  /* ---------- <lum-quantity> ------------------------------------------ */
  class LumQuantity extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="number"]');
      this.querySelectorAll('button').forEach((btn) =>
        btn.addEventListener('click', () => {
          const dir = btn.dataset.dir === 'up' ? 1 : -1;
          const min = +this.input.min || 1;
          this.input.value = Math.max(min, (+this.input.value || min) + dir);
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
        })
      );
    }
  }
  customElements.define('lum-quantity', LumQuantity);

  /* ---------- <lum-gallery> ------------------------------------------- */
  class LumGallery extends HTMLElement {
    connectedCallback() {
      this.thumbs = this.querySelectorAll('[data-thumb]');
      this.main = this.querySelector('[data-main]');
      this.counter = this.querySelector('[data-counter]');
      this.thumbs.forEach((t, i) =>
        t.addEventListener('click', () => this.show(i))
      );
    }
    show(i) {
      this.thumbs.forEach((t, idx) => t.classList.toggle('is-active', idx === i));
      const slides = this.main.querySelectorAll('[data-slide]');
      slides.forEach((s, idx) => {
        s.hidden = idx !== i;
        if (idx === i) s.setAttribute('aria-current', 'true');
        else s.removeAttribute('aria-current');
      });
      if (this.counter) this.counter.textContent = `${i + 1} / ${slides.length}`;
    }
  }
  customElements.define('lum-gallery', LumGallery);

  /* ---------- <lum-light-picker> -------------------------------------- */
  class LumLightPicker extends HTMLElement {
    connectedCallback() {
      this.options = this.querySelectorAll('[data-light]');
      this.preview = this.querySelector('[data-light-preview]');
      this.title = this.querySelector('[data-light-title]');
      this.desc = this.querySelector('[data-light-desc]');
      this.label = this.querySelector('[data-light-label]');
      this.options.forEach((o) =>
        o.addEventListener('click', () => this.select(o))
      );
    }
    select(opt) {
      const color = opt.dataset.color;
      const name = opt.dataset.name;
      const nm = opt.dataset.nm;
      const desc = opt.dataset.desc;
      const idx = opt.dataset.index;
      this.options.forEach((o) => o.classList.toggle('is-active', o === opt));
      if (this.preview) this.preview.style.setProperty('--lum-light-color', color);
      if (this.title) this.title.textContent = name;
      if (this.desc) this.desc.textContent = desc;
      if (this.label) this.label.textContent = `MODE ${String(idx).padStart(2, '0')} / ${this.options.length} · ${nm.toUpperCase()}`;
    }
  }
  customElements.define('lum-light-picker', LumLightPicker);

  /* ---------- <lum-sticky-atc> ---------------------------------------- */
  class LumStickyAtc extends HTMLElement {
    connectedCallback() {
      this.trigger = document.querySelector(this.dataset.triggerSelector || '[data-sticky-trigger]');
      if (!this.trigger || !('IntersectionObserver' in window)) {
        this.show();
        return;
      }
      const io = new IntersectionObserver(
        ([entry]) => {
          this.toggleAttribute('data-visible', !entry.isIntersecting);
        },
        { threshold: 0 }
      );
      io.observe(this.trigger);
    }
    show() { this.setAttribute('data-visible', ''); }
  }
  customElements.define('lum-sticky-atc', LumStickyAtc);

  /* ---------- <lum-bundle> -------------------------------------------- */
  // Syncs bundle radio selection with the buy-form's quantity input so the
  // merchant can present a "1 / 2 / 3 pack" UI mapped to variants.
  class LumBundle extends HTMLElement {
    connectedCallback() {
      this.form = document.querySelector(this.dataset.formSelector || '[data-product-form]');
      this.querySelectorAll('input[type="radio"]').forEach((r) =>
        r.addEventListener('change', () => this.sync(r))
      );
    }
    sync(input) {
      if (!this.form) return;
      const qtyInput = this.form.querySelector('input[name="quantity"]');
      const variantInput = this.form.querySelector('input[name="id"]');
      if (input.dataset.variantId && variantInput) variantInput.value = input.dataset.variantId;
      if (qtyInput && input.dataset.qty) qtyInput.value = input.dataset.qty;
      this.form.dispatchEvent(new CustomEvent('lum:bundle-change', { detail: { input } }));
    }
  }
  customElements.define('lum-bundle', LumBundle);

  /* ---------- <lum-countdown> ----------------------------------------- */
  class LumCountdown extends HTMLElement {
    connectedCallback() {
      const hours = +this.dataset.hours || 3;
      const minutes = +this.dataset.minutes || 47;
      this.target = Date.now() + (hours * 60 + minutes) * 60_000;
      this.tick();
      this.timer = setInterval(() => this.tick(), 60_000);
    }
    disconnectedCallback() { clearInterval(this.timer); }
    tick() {
      const diff = Math.max(0, this.target - Date.now());
      const h = Math.floor(diff / 3_600_000);
      const m = Math.floor((diff % 3_600_000) / 60_000);
      this.textContent = `${h}h ${m}m`;
    }
  }
  customElements.define('lum-countdown', LumCountdown);
})();
